import * as THREE from 'three'

export default class Smoke
{
    constructor(_options)
    {
        // Options
        this.time = _options.time
        this.scene = _options.scene
        this.debug = _options.debug
        this.parent = _options.parent || null // Parent object to attach to (e.g., car)
        this.offset = _options.offset || new THREE.Vector3(0, 0, 0) // Local offset from parent
        this.rotation = _options.rotation || new THREE.Euler(0, 0, 0) // Local rotation

        // Set up
        this.container = new THREE.Object3D()
        
        // If parent provided, attach to it; otherwise add to scene
        if(this.parent)
        {
            this.parent.add(this.container)
            this.container.position.copy(this.offset)
            this.container.rotation.copy(this.rotation)
        }
        else if(this.scene)
        {
            this.scene.add(this.container)
        }

        // Debug
        if(this.debug)
        {
            this.debugFolder = this.debug.addFolder('smoke')
        }

        this.setSmoke()
    }

    setSmoke()
    {
        // Create smoke texture
        const canvas = document.createElement('canvas')
        canvas.width = 128
        canvas.height = 128
        const ctx = canvas.getContext('2d')
        const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
        gradient.addColorStop(0, 'rgba(50,50,50,1)')    // Dark gray center (visible but dark)
        gradient.addColorStop(0.5, 'rgba(30,30,30,0.6)')  // Darker middle
        gradient.addColorStop(1, 'rgba(0,0,0,0)')        // Transparent edge
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, 128, 128)
        
        const texture = new THREE.Texture(canvas)
        texture.needsUpdate = true

        // Particle system - more particles for realistic smoke
        const particleCount = 100
        const particles = new THREE.BufferGeometry()
        const positions = new Float32Array(particleCount * 3)
        
        this.velocities = []
        this.lifetimes = []

        // Initialize particles at center
        for(let i = 0; i < particleCount; i++)
        {
            positions[i * 3] = 0
            positions[i * 3 + 1] = 0
            positions[i * 3 + 2] = 0
            
            this.velocities.push({
                x: (Math.random() - 0.5) * 0.02, // Sideways spread
                y: (Math.random() - 0.5) * 0.02, // Up/down spread
                z: -(Math.random() * 0.01 + 0.0)    // Backward movement (Z-) - reduced to travel less
            })
            
            // Stagger lifetimes so particles reset at different times for continuous generation
            this.lifetimes.push(Math.random() * 80)
        }

        particles.setAttribute('position', new THREE.BufferAttribute(positions, 3))

        const material = new THREE.PointsMaterial({
            size: 1.5,  // Smaller, more particles = smoother smoke
            map: texture,
            transparent: true,
            opacity: 0.05,
            blending: THREE.NormalBlending,
            depthWrite: false,
            sizeAttenuation: true  // Particles get smaller with distance
        })

        this.particleSystem = new THREE.Points(particles, material)
        this.container.add(this.particleSystem)

        // Update on tick
        this.time.on('tick', () =>
        {
            this.update()
        })
    }

    update()
    {
        if(!this.particleSystem) return

        const positions = this.particleSystem.geometry.attributes.position.array

        for(let i = 0; i < this.velocities.length; i++)
        {
            this.lifetimes[i]++

            positions[i * 3] += this.velocities[i].x
            positions[i * 3 + 1] += this.velocities[i].y
            positions[i * 3 + 2] += this.velocities[i].z

            // Continuous generation: reset particle when lifetime expires
            // Particles reset at different times creating continuous smoke flow
            if(this.lifetimes[i] > 80)
            {
                // Reset to origin with slight random offset for natural look
                positions[i * 3] = (Math.random() - 0.5) * 0.5
                positions[i * 3 + 1] = 0
                positions[i * 3 + 2] = 0
                // Reset lifetime to 0 so it immediately starts moving again
                this.lifetimes[i] = 0
            }
        }

        this.particleSystem.geometry.attributes.position.needsUpdate = true
    }
}

